import {beforeAuthStateChanged, onAuthStateChanged} from "firebase/auth";
import { deleteDoc, doc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from "firebase/messaging";
import { app, auth, db } from "../firebaseConfig";

// Public certificate for church-fundraising-rccg; the private key stays in Firebase.
const vapidKey = String(process.env.REACT_APP_FIREBASE_VAPID_KEY || (app?.options?.projectId === 'church-fundraising-rccg' ? "BGTur1pKVoTUCcTeiltXnKLm-6cpuj7mFZ4l2NVFeTT-jmJNhfAjDs7jfwsIqn7EDF9NTx3cGu7G1xVvikOeJ8M" : '')).trim();

const registrations = new Map();
const pendingRegistrations = new Map();
const ownerKey = "lightly.push.owner";
let boundaryInstalled = false;
let generation = 0;
let switching = false;
let tokenCleanup = null;

function previousOwner() { try { return localStorage.getItem(ownerKey) || ""; } catch { return ""; } }
function rememberOwner(uid) { try { if (uid) localStorage.setItem(ownerKey, uid); else localStorage.removeItem(ownerKey); } catch {} }

export function installPushAccountBoundary() {
  if (boundaryInstalled) return;
  boundaryInstalled = true;
  beforeAuthStateChanged(auth, async (nextUser) => {
    const oldUid = auth.currentUser?.uid || previousOwner();
    if (!oldUid || oldUid === nextUser?.uid) return;
    switching = true;
    generation += 1;
    // Invalidate pending registrations locally; permission/network waits must not hold Auth.
    // Recover this device's other organization registrations after a reload.
    try {
      for (let n = 0; n < localStorage.length; n++) {
        const key = localStorage.key(n);
        if (key?.startsWith("lightly.push.") && key.endsWith("." + oldUid)) {
          const orgId = key.slice("lightly.push.".length, -(oldUid.length + 1));
          const id = localStorage.getItem(key);
          if (orgId && id) registrations.set(orgId + ":" + oldUid, {orgId, id});
        }
      }
    } catch {}
    // Browser-level unsubscription revokes delivery locally, even while offline.
    // Do this before waiting on Firestore/FCM, whose promises can remain pending.
    const worker = await navigator.serviceWorker?.getRegistration?.();
    const subscription = await worker?.pushManager?.getSubscription();
    if (subscription && !(await subscription.unsubscribe())) throw new Error("Device notifications could not be disconnected. Try switching accounts again when connected.");
    const cleanup = Promise.allSettled([
      ...[...registrations.values()].map(({orgId, id}) => deleteDoc(doc(db, "organizations", orgId, "pushSubscriptions", id))),
      tokenCleanup ||= (async () => { if (await isSupported()) await deleteToken(getMessaging(app)); })().finally(() => { tokenCleanup = null; }),
    ]);
    // Give online cleanup a brief opportunity while the old identity is current;
    // local delivery is already disconnected, so offline writes cannot trap sign-out.
    await boundedCleanup(cleanup);
    for (const {orgId} of registrations.values()) { try { localStorage.removeItem(`lightly.push.${orgId}.${oldUid}`); } catch {} }
    registrations.clear();
    rememberOwner("");
  }, () => { switching = false; });
  onAuthStateChanged(auth, () => { switching = false; });
}

function boundedCleanup(cleanup) {
  return new Promise(resolve => {
    const timer = setTimeout(resolve, 500);
    cleanup.finally(() => { clearTimeout(timer); resolve(); });
  });
}

async function tokenId(token) {
  const bytes = new TextEncoder().encode(token);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, "0")).join("").slice(0, 40);
}

export async function enableAnnouncementPush(orgId, {silent = false} = {}) {
  installPushAccountBoundary();
  const uid = auth.currentUser?.uid;
  if (silent && (typeof Notification === 'undefined' || Notification.permission !== 'granted')) return null;
  try { if (silent && localStorage.getItem(`lightly.push-disabled.${orgId}.${uid}`)) return null; } catch {}
  if (!uid || switching) throw new Error("Sign in before enabling notifications.");
  if (tokenCleanup) throw new Error("Notification cleanup is still in progress. Try enabling notifications again shortly.");
  const key = `${orgId}:${uid}`;
  if (pendingRegistrations.has(key)) return pendingRegistrations.get(key);
  const requestGeneration = generation;
  const request = registerPush(orgId, uid, requestGeneration, silent);
  pendingRegistrations.set(key, request);
  try { return await request; }
  finally { pendingRegistrations.delete(key); }
}

async function registerPush(orgId, uid, requestGeneration, silent) {
  if (!vapidKey) throw new Error('Phone notifications need a configured Web Push public key for this project.');
  if (!(await isSupported()) || !("serviceWorker" in navigator) || !("Notification" in window)) throw new Error("Push notifications are not supported by this browser.");
  if (Notification.permission === "denied") throw new Error("Notifications are blocked. Allow them in your browser settings to enable alerts.");
  if (silent && Notification.permission !== 'granted') return null;
  const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Notification permission was not granted.");
  const registration = await navigator.serviceWorker.register("/service-worker.js", {updateViaCache: "none"});
  await navigator.serviceWorker.ready;
  const messaging = getMessaging(app);
  const options = {serviceWorkerRegistration: registration, ...(vapidKey ? {vapidKey} : {})};
  const token = await getToken(messaging, options);
  if (!token) throw new Error("This browser could not create a notification subscription.");
  if (auth.currentUser?.uid !== uid || switching || generation !== requestGeneration) throw new Error("The signed-in account changed. Enable notifications again.");
  const id = `${uid}_${await tokenId(token)}`;
  if (auth.currentUser?.uid !== uid || switching || generation !== requestGeneration) throw new Error("The signed-in account changed. Enable notifications again.");
  const disabled=()=>{try{return silent && localStorage.getItem(`lightly.push-disabled.${orgId}.${uid}`);}catch{return false;}};
  if(disabled())return null;
  await setDoc(doc(db, "organizations", orgId, "pushSubscriptions", id), {
    uid, token, active: true, permission, origin: window.location.origin, platform: navigator.platform || "web",
    userAgent: navigator.userAgent.slice(0, 500), createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  }, {merge: true});
  if (auth.currentUser?.uid !== uid || switching || generation !== requestGeneration) {
    void deleteDoc(doc(db, "organizations", orgId, "pushSubscriptions", id)).catch(() => {});
    throw new Error("The signed-in account changed. Enable notifications again.");
  }
  if(disabled()){void deleteDoc(doc(db,'organizations',orgId,'pushSubscriptions',id)).catch(()=>{});return null;}
  let previousId; try { previousId = localStorage.getItem(`lightly.push.${orgId}.${uid}`); } catch {}
  if (previousId && previousId !== id) void deleteDoc(doc(db, 'organizations', orgId, 'pushSubscriptions', previousId)).catch(() => {});
  registrations.set(orgId + ":" + uid, {orgId, id});
  rememberOwner(uid);
  try {localStorage.setItem(`lightly.push.${orgId}.${uid}`, id);localStorage.removeItem(`lightly.push-disabled.${orgId}.${uid}`);} catch {}
  return {id, token};
}

// Push subscriptions are organization/device scoped and are shared by
// announcements and private team alerts. Keep the legacy export while newer
// surfaces use a name that accurately describes the capability.
export const enableOrganizationPush = enableAnnouncementPush;
export const organizationPushCapability = announcementPushCapability;

export async function disableAnnouncementPush(orgId, subscription) {
  if (!subscription?.id) return;
  // A device token is shared across organizations. Remove only this org's
  // subscription; globally deleting it would silently disable the other orgs.
  try { localStorage.setItem(`lightly.push-disabled.${orgId}.${auth.currentUser?.uid}`, 'true'); localStorage.removeItem(`lightly.push.${orgId}.${auth.currentUser?.uid}`); } catch {}
  await deleteDoc(doc(db, "organizations", orgId, "pushSubscriptions", subscription.id));
}

export function announcementPushCapability() {
  return typeof window !== "undefined" && "Notification" in window && "serviceWorker" in navigator;
}

export function subscribeOrganizationPush(orgId, callback) {
  let stop = () => {}, active = true, revision = 0;
  const off = onAuthStateChanged(auth, user => {
    const request = ++revision;
    stop(); callback('unregistered');
    const uid = user?.uid;
    const publish = state => {
      if (active && request === revision && auth.currentUser?.uid === uid) callback(state);
    };
    let id; try { id = localStorage.getItem(`lightly.push.${orgId}.${uid}`); } catch {}
    if (!uid || typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const listen = currentId => {
      stop();
      stop = onSnapshot(doc(db,'organizations',orgId,'pushSubscriptions',currentId), snap => publish(snap.exists() && snap.data().active === true ? 'registered' : 'unregistered'), () => publish('unknown'));
    };
    if (id) listen(id);
    enableAnnouncementPush(orgId, {silent:true}).then(result => {
      if (!active || request !== revision || auth.currentUser?.uid !== uid || !result) return;
      publish('registered');
      if (!id || result.id !== id) listen(result.id);
    }).catch(() => { if (!id) publish('unknown'); });
  });
  return () => { active = false; revision += 1; off(); stop(); };
}
export function subscribeForegroundPush(callback) {
  let stopped=false,off;
  isSupported().then(supported => {if(supported && !stopped)off=onMessage(getMessaging(app),payload=>{if(stopped || switching || !auth.currentUser?.uid || (payload.data?.recipientUid && payload.data.recipientUid!==auth.currentUser.uid))return;callback(payload);});}).catch(()=>{});
  return ()=>{stopped=true;off?.();};
}

// Refresh previously enabled organizations on each app opening, without prompting.
export async function refreshKnownOrganizationPush() {
  const uid=auth.currentUser?.uid;
  if(!uid || typeof Notification==='undefined' || Notification.permission!=='granted')return;
  const orgIds=[];
  try { for(let i=0;i<localStorage.length;i++){const key=localStorage.key(i);if(key?.startsWith('lightly.push.') && key.endsWith('.'+uid))orgIds.push(key.slice('lightly.push.'.length,-(uid.length+1)));} } catch {}
  await Promise.allSettled(orgIds.filter(Boolean).map(orgId=>enableAnnouncementPush(orgId,{silent:true})));
}
